/**
 * Клиентское зеркало правил СОУТ (api-contract §3d / canon service-sout). Чистые функции —
 * тестируются изолированно (см. sout.test.ts). Те же правила сервер применяет авторитетно на
 * submit; здесь — для мгновенной подсветки/блокировки в визарде и гейта выгрузки в Excel.
 */
import { isValidEmail, isValidPhone, isValidSnils, validateFio } from '@/lib/validation'
import { ACCIDENT_SEVERITIES, SCHEDULES } from '@/lib/sout-dictionaries'
import type { StatusRole } from '@/design-system/tokens/status'
import type {
  CommissionMember,
  Responsible,
  SoutAccident,
  SoutConditions,
  SoutDashboardStatus,
  SoutLine,
  SoutPerson,
  SoutPreviousSout,
  SoutDeclarationWorkplace,
  SoutStatement,
  SoutStatementMatch,
  SoutStatementRow,
  SoutTransport,
} from '@/lib/api'
import { isUnspecifiedDepartment } from '@/lib/department'

/** Дефолт тяжести НС (IC-2, план фазы 2 §решение 3) — дословно из канона (без «ё»). */
export const DEFAULT_ACCIDENT_SEVERITY: string = ACCIDENT_SEVERITIES[0]

/** Дефолт графика работы (IC-2, minor-1) — как `newWorkplace()` в прототипе (`sout.html:472`). */
export const DEFAULT_SCHEDULE: string = SCHEDULES[0]

// --- Фабрики пустых сущностей (локальное состояние визарда) ---

export function emptyCommissionMember(isChair = false): CommissionMember {
  // personId '' — подписант ещё не выбран из сотрудников (p3-sout-commission D2/D6);
  // выбор/гейт «требует перевыбора» — поздние волны.
  return { personId: '', fullName: '', position: '', isChair }
}

export function emptyResponsible(): Responsible {
  return { personId: '', fullName: '', position: '', phone: '', email: '' }
}

export function emptyTransport(): SoutTransport {
  return { brand: '', year: 0, massLaden: 0, massUnladen: 0, plate: '' }
}

export function emptyAccident(): SoutAccident {
  return { description: '', date: '', severity: DEFAULT_ACCIDENT_SEVERITY }
}

/**
 * Черновик строки для страницы «Добавить вручную» (p3-sout-workplaces §14, Волна 7) — пока
 * нет серверной строки (создаётся ОДНИМ сохранением, см. `soutApi.addManualLine`). Значения
 * режима работы (`shiftHours`/`weekHours`/`schedule`) намеренно пустые/нулевые — дефолты
 * `8`/`40`/«5/2» (§14 «предзаполнить только режим работы») проставляет уже существующий
 * эффект `WorkplaceForm` (D9 + §7 «для нового места автоматически установлены значения»),
 * который срабатывает при монтировании независимо от `manual`; отдельного механизма
 * предзаполнения здесь не требуется. `id: 'new'` — стабильный на весь жизненный цикл
 * страницы (эффект `WorkplaceForm` держит его в deps, но он не меняется до навигации).
 */
export function emptyManualSoutLine(): SoutLine {
  return {
    id: 'new',
    workplaceId: null,
    personId: null,
    carryoverFromOrderId: null,
    person: { fullName: '', snils: '', gender: '', birthDate: '', isMinor: false, isDisabled: false },
    conditions: {
      departmentName: '',
      actualAddress: '',
      position: '',
      rmType: 'D',
      shiftHours: 0,
      weekHours: 0,
      schedule: '',
      scheduleOther: '',
      hadPreviousSout: false,
      rmNumber: '',
      previousClass: '',
      compensations: {
        extraPay: false,
        extraVacation: false,
        shortWeek: false,
        milk: false,
        earlyPension: false,
        medical: false,
      },
      equipment: '',
      materials: '',
      transport: [],
      accidents: [],
      lineSource: 'manual',
    },
    valid: false,
    missing: [],
  }
}

// --- График работы (IC-2, §решение 8) ---

/**
 * Разрешение графика для отображения в Select+«Иной» (back-compat со снимками ДО этого
 * change, где график был свободным текстом):
 * - пустой график ("" — новая строка до выбора / старый необязательный пустой) → канон-дефолт
 *   «5/2» (прототип `newWorkplace` ставит его, `sout.html:472`), НЕ «Иной»;
 * - значение из справочника SCHEDULES — как есть;
 * - непустой текст ВНЕ справочника (легаси) → «Иной» + исходный текст в `scheduleOther`.
 * ЧИСТО клиентская нормализация отображения — сам снимок НЕ переписывается (правится только
 * если пользователь реально отредактирует и сохранит форму заново).
 */
export function resolveScheduleDisplay(
  c: Pick<SoutConditions, 'schedule' | 'scheduleOther'>,
): { schedule: string; scheduleOther: string } {
  if (!c.schedule.trim()) {
    return { schedule: DEFAULT_SCHEDULE, scheduleOther: c.scheduleOther }
  }
  if ((SCHEDULES as readonly string[]).includes(c.schedule)) {
    return { schedule: c.schedule, scheduleOther: c.scheduleOther }
  }
  return { schedule: 'Иной', scheduleOther: c.scheduleOther || c.schedule }
}

// --- Выгрузка рабочих мест заказа в Excel (centrika-docs!89) ---

/**
 * Имя файла выгрузки рабочих мест заказа СОУТ (требование, дословно):
 * «Рабочие места СОУТ — <номер заказа>.xlsx». Тире — длинное («—»), как в требовании.
 * Зеркало серверного `exports.SoutWorkplacesFileName` (файл формирует сервер, но браузеру
 * имя задаёт скачивающий код).
 */
export function soutWorkplacesFileName(orderNumber: string): string {
  return `Рабочие места СОУТ — ${orderNumber}.xlsx`
}

/** Сообщение о неудачной выгрузке рабочих мест (требование, дословно). */
export const SOUT_WORKPLACES_EXPORT_ERROR = 'Не удалось скачать файл. Повторите попытку.'

// --- СНИЛС (IC-2, п.10 — живая маска) ---

/**
 * Маска СНИЛС — единый источник для всего кабинета (centrika-docs!46, `common.md` →
 * «Поля СНИЛС»): реэкспорт из `lib/snils`, чтобы у анкеты СОУТ и остальных полей была
 * одна реализация, а не две расходящиеся копии.
 */
export { formatSnils } from './snils'

// --- Телефон (IC-3, п.5 — живая маска ответственного) ---

/**
 * Живое маскирование телефона РФ по мере ввода (по мотивам `formatPhone` из `sout.html`):
 * «+7 (999) 123-45-67». Оставляет только цифры, отбрасывает ведущий код страны 7/8, берёт не
 * более 10 национальных цифр и группирует их скобками/дефисами. Пустой ввод → "".
 *
 * В React input контролируемый и переформатируется на КАЖДЫЙ ввод — значение подаётся в
 * `formatPhone` повторно (уже с префиксом «+7»). Поэтому, в отличие от прототипа (там код
 * страны отбрасывался лишь при 11 цифрах, т.к. в state хранились «сырые» национальные цифры),
 * здесь ведущий 7/8 отбрасывается ВСЕГДА — это делает функцию идемпотентной
 * (`formatPhone(formatPhone(x)) === formatPhone(x)`). Для мобильного номера (начинается с 9)
 * поведение идентично прототипу. Хранится уже отформатированная строка; проверки телефона
 * (`isValidPhone`/BE `ValidPhoneRU`) игнорируют пробелы/скобки/дефисы.
 */
export function formatPhone(value: string): string {
  let d = value.replace(/\D/g, '')
  if (d[0] === '7' || d[0] === '8') d = d.slice(1)
  d = d.slice(0, 10)
  if (!d) return ''
  let r = '+7'
  if (d.length > 0) r += ` (${d.slice(0, Math.min(3, d.length))}`
  if (d.length >= 3) r += ')'
  if (d.length > 3) r += ` ${d.slice(3, Math.min(6, d.length))}`
  if (d.length > 6) r += `-${d.slice(6, Math.min(8, d.length))}`
  if (d.length > 8) r += `-${d.slice(8, 10)}`
  return r
}

// --- Комиссия ---

/**
 * Склонение слова «человек» для количества `n` (p3-sout-commission §2.3): «человек» —
 * для 1/5–20/оканчивающихся на 0 или 5–9 и для 11–14; «человека» — для оканчивающихся на 2–4
 * (кроме 12–14). То есть либо «человек» (им. ед. + род. мн.), либо «человека» (род. ед.).
 * Учитывает 21 (человек), 22–24 (человека), 12–14 (человек).
 */
export function peopleWord(n: number): string {
  const abs = Math.abs(n)
  const mod100 = abs % 100
  const mod10 = abs % 10
  if (mod10 === 1 && mod100 !== 11) return 'человек'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'человека'
  return 'человек'
}

export interface CommissionSummary {
  /** Итоговое сообщение о составе (показывается всегда под членами комиссии). */
  message: string
  /** Состав корректен → gate «Далее»/подача открыты. */
  ok: boolean
  /** Стиль вывода: успех или ошибка. */
  tone: 'success' | 'error'
}

/**
 * Итог по составу комиссии (centrika-docs, component.create-sout-order.commission, MR !66;
 * sout-commission-odd). Минимальный состав — один председатель (члены комиссии необязательны,
 * «минимум 3» снят по !66); общее число участников вместе с председателем — НЕЧЁТНОЕ (заказчица
 * 15.10.2026: «нужно просто „нечётное“»; 426-ФЗ ст. 9): 1, 3, 5… Требуется: председатель выбран
 * (реальный сотрудник `personId`); без повторов `personId`; в каждой отображаемой строке члена
 * выбран сотрудник (иначе строку удалить); нечётное число участников. Тексты ошибок (по порядку):
 *  - председатель не выбран → «Выберите председателя комиссии.»
 *  - дубль сотрудника → «Один сотрудник не может занимать несколько мест в комиссии.»
 *  - строка члена без выбора → «Выберите сотрудника в каждой строке комиссии.»
 *  - чётное число → «Сейчас в комиссии N человек вместе с председателем — количество должно быть
 *    нечётным.» (дословно как на сервере, `soutCommissionEvenMessage`)
 * При корректном составе `message` пуст (success-текст в форме больше не показывается).
 */
export function commissionSummary(members: CommissionMember[]): CommissionSummary {
  const chairs = members.filter((m) => m.isChair)
  const chairOk = chairs.length === 1 && Boolean((chairs[0]?.personId ?? '').trim())
  const ids = members.map((m) => (m.personId ?? '').trim()).filter(Boolean)
  const hasDup = new Set(ids).size !== ids.length
  const pending = members.some((m) => !(m.personId ?? '').trim())
  const ok = chairOk && !hasDup && !pending
  if (!chairOk) return { ok: false, tone: 'error', message: 'Выберите председателя комиссии.' }
  if (hasDup) {
    return {
      ok: false,
      tone: 'error',
      message: 'Один сотрудник не может занимать несколько мест в комиссии.',
    }
  }
  if (pending) {
    return { ok: false, tone: 'error', message: 'Выберите сотрудника в каждой строке комиссии.' }
  }
  if (members.length % 2 === 0) {
    const n = members.length
    return {
      ok: false,
      tone: 'error',
      message: `Сейчас в комиссии ${n} ${peopleWord(n)} вместе с председателем — количество должно быть нечётным.`,
    }
  }
  return { ok, tone: 'success', message: '' }
}

/**
 * Валидация состава комиссии (§3d / p3-sout-commission §2.3). Зеркало серверных правил на submit.
 * Возвращает текст ошибки (итоговое сообщение) или null, если состав корректен.
 */
export function validateCommission(members: CommissionMember[]): string | null {
  const s = commissionSummary(members)
  return s.ok ? null : s.message
}

// --- Ответственный ---

export type ResponsibleErrors = Partial<Record<'personId' | 'fullName' | 'phone' | 'email', string>>

/**
 * Валидация ответственного (§3d): `personId` обязателен. Ответственным может быть любой сотрудник
 * организации, не только член комиссии (centrika-docs, component.create-sout-order.responsible,
 * MR !11); существование и принадлежность сотрудника проверяет сервер. ФИО (ValidFIO, зеркалит
 * замороженный текст карточки), телефон (+7/10 цифр), email (@ и точка) — как раньше.
 */
export function validateResponsible(r: Responsible): ResponsibleErrors {
  const errors: ResponsibleErrors = {}
  const personId = (r.personId ?? '').trim()
  // Ответственным может быть любой сотрудник организации, не только член комиссии (centrika-docs,
  // component.create-sout-order.responsible, MR !11). Существование/принадлежность проверяет сервер.
  if (!personId) {
    errors.personId = 'Выберите ответственного'
  }
  if (!validateFio(r.fullName)) errors.fullName = 'Проверьте ФИО (минимум 3 слова, без инициалов)'
  if (!isValidPhone(r.phone)) errors.phone = 'Номер должен начинаться с +7 и содержать 10 цифр'
  if (!isValidEmail(r.email)) errors.email = 'Проверьте email (нужны «@» и точка)'
  return errors
}

// --- Рабочие места (список/счётчик, p3-sout-workplaces §1/§3) ---

/**
 * Склонение «рабочее место»/«рабочих места»/«рабочих мест» вместе с числом — счётчик
 * «Добавлено: N рабочих мест» (§1) и количество в заголовке группы подразделения (§3).
 * Правило то же, что и канон-прототип (`wpPlural` из `sout.html`).
 */
export function workplacesCountLabel(n: number): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} рабочее место`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} рабочих места`
  return `${n} рабочих мест`
}

// --- Служебный транспорт ---

// Госномер — свободный текст: на рабочем месте бывают не только легковые номера, но и тракторы
// (2685УО 23), прицепы, мотоциклы, иностранные ТС. Формат не проверяем (решение после MR !67),
// обязательность — как раньше (зеркало бэкенда, `soutTransportValid`: непустая строка).

/** Госномер заполнен (формат не проверяется). */
export function isValidPlate(plate: string): boolean {
  return (plate ?? '').trim() !== ''
}

/** Текст ошибки поля «Гос. номер» — совпадает с серверным («укажите государственный номер»). */
export const PLATE_ERROR = 'Укажите государственный номер.'

/**
 * Валидность записи транспорта (p3-sout-workplaces §12/§16, зеркало `soutTransportValid`
 * `backend/internal/http/dto_sout.go`): для добавленной записи обязательны марка, гос. номер
 * и год выпуска (4 цифры, диапазон 1900..тек+1); массы (с грузом/без груза) НЕОБЯЗАТЕЛЬНЫ —
 * 0 = «не заполнено», не ошибка; заполненная масса должна быть положительной (отрицательная —
 * невалидна). `now` — для тестов.
 *
 * Отклонение от более раннего IC-2 решения «гос. номер необязателен»: ТЗ §12 дословно требует
 * гос. номер для добавленной записи, и backend (Волна 2, `soutTransportValid`) это уже
 * реализует — зеркалим авторитетное серверное правило.
 */
export function isValidTransport(t: SoutTransport, now: Date = new Date()): boolean {
  if (!t.brand.trim()) return false
  // Гос. номер обязателен (формат не проверяется).
  if (!isValidPlate(t.plate)) return false
  const maxYear = now.getFullYear() + 1
  if (!Number.isInteger(t.year) || t.year < 1900 || t.year > maxYear) return false
  // Массы обязательны (centrika-docs, page.create-sout-order.new-workplace, MR !15 — разворот
  // прежнего «массы необязательны», реш. №6): при заполненной записи масса > 0.
  return t.massLaden > 0 && t.massUnladen > 0
}

// --- Несчастные случаи (§10/§16) ---

/** Предел записей НС (§10 «0–50 записей») — зеркало `soutAccidentsMax` бэкенда. */
export const SOUT_ACCIDENTS_MAX = 50

/** Добавленная запись НС обязана иметь дату, тяжесть и описание (§10/§16). */
export function isValidAccident(a: SoutAccident): boolean {
  return Boolean(a.date.trim() && a.severity.trim() && a.description.trim())
}

/** Список НС валиден: 0–50 записей, каждая добавленная — полностью заполнена (§10/§16). */
function accidentsValid(accidents: SoutAccident[]): boolean {
  if (accidents.length > SOUT_ACCIDENTS_MAX) return false
  return accidents.every(isValidAccident)
}

// --- Блок «Сотрудник» (§6/§16) ---

/**
 * «Клиент начал заполнять данные сотрудника» — хотя бы одно поле блока «Сотрудник» непустое.
 * После MR !13 в правилах «дозаполнить» не участвует (там триггер — ФИО, `soutPersonNamed` на
 * сервере); остаётся признаком для подсказки «Ранее указано: <ФИО>» у строк без `personId`
 * (§15). `isMinor` не участвует: чекбокс «Сотрудник младше 18 лет» (centrika-docs!93) сам по
 * себе не означает «клиент начал заполнять сотрудника».
 */
export function isPersonPartiallyFilled(p: SoutPerson): boolean {
  return Boolean(p.fullName.trim() || p.snils.trim() || p.gender || p.birthDate.trim() || p.isDisabled)
}

/**
 * СНИЛС в сравнимом виде — только 11 цифр (centrika-docs!94: «СНИЛС сравниваются по 11 цифрам
 * без учёта пробелов и дефисов»). Разделители маски `000-000-000 00` на сравнение не влияют.
 */
function snilsKey(snils: string): string {
  return snils.replace(/\D/g, '')
}

/**
 * Уникальность СНИЛС внутри заказа (centrika-docs!101, `page.create-sout-order.new-workplace`
 * → СНИЛС → «Уникальность СНИЛС в заказе»). Выполняется прямо в форме, по уже загруженным
 * строкам текущего заказа — без сохранения формы и без обращения к серверу.
 *
 * - Проверка идёт, только когда заполнено ФИО и СНИЛС введён полностью и корректно
 *   (`isValidSnils`); иначе `false` — сообщение не показывается.
 * - Редактируемое рабочее место не сравнивается само с собой (исключение по `id`).
 * - Совпадением считается тот же СНИЛС (11 цифр) НЕЗАВИСИМО от указанных ФИО: до
 *   centrika-docs!101 одинаковый СНИЛС у одинакового ФИО конфликтом не считался, теперь
 *   СНИЛС в разных рабочих местах одного заказа обязаны различаться.
 */
export function hasSnilsOrderConflict(
  current: Pick<SoutLine, 'person'> & { id?: string },
  orderLines: ReadonlyArray<Pick<SoutLine, 'person'> & { id: string }>,
): boolean {
  if (!current.person.fullName.trim()) return false
  if (!isValidSnils(current.person.snils)) return false
  const key = snilsKey(current.person.snils)
  return orderLines.some(
    (other) => other.id !== current.id && snilsKey(other.person.snils) === key,
  )
}

// --- Строка рабочего места ---

/**
 * Список незаполненных/невалидных обязательных полей строки — единые правила «дозаполнить»
 * (p3-sout-workplaces §16, design D6). Зеркало серверного `soutLineIssues`
 * (`backend/internal/http/dto_sout.go`):
 * - всегда обязательны: подразделение, адрес, должность, смена>0, часы>0, график, оборудование,
 *   материалы;
 * - поля сотрудника (ФИО/СНИЛС/пол; даты рождения в форме больше нет — centrika-docs!93) —
 *   только если сотрудник назначен (`personId`) ИЛИ
 *   заполнено ФИО (centrika-docs, `page.create-sout-order.new-workplace`, MR !13: «если поле
 *   ФИО не заполнено, обязательных полей в блоке нет; рабочее место сохраняется без
 *   сотрудника»). Прежнее правило «начат любой из полей блока» и обязательность сотрудника
 *   при ручном создании (p3-critical-fixes №3г) требованием сняты;
 * - предыдущая СОУТ (класс/№ РМ) — ВСЕГДА необязательна (реш. заказчика №2);
 * - транспорт/НС — пустые списки допустимы; добавленная запись обязана быть полной
 *   (`isValidTransport`/`isValidAccident`); НС — не более `SOUT_ACCIDENTS_MAX`.
 *
 * `personId` необязателен в самом типе параметра (back-compat с фикстурами тестов, которые
 * передают только `person`/`conditions`) — отсутствующий трактуется как «не назначен».
 */
export function computeLineMissing(
  line: Pick<SoutLine, 'person' | 'conditions'> & { personId?: string | null },
): string[] {
  const m: string[] = []
  const c = line.conditions
  // «Не указано» (training-listeners-count) — тоже не заполнено: для СОУТ подразделение обязательно.
  if (isUnspecifiedDepartment(c.departmentName)) m.push('departmentName')
  if (!c.actualAddress.trim()) m.push('actualAddress')
  if (!c.position.trim()) m.push('position')
  if (!(c.shiftHours > 0)) m.push('shiftHours')
  if (!(c.weekHours > 0)) m.push('weekHours')
  if (!c.schedule.trim()) m.push('schedule')
  // График «Иной» требует комментария с описанием режима (centrika-docs, MR !69).
  if (c.schedule.trim() === 'Иной' && !c.scheduleOther.trim()) m.push('scheduleOther')
  if (!c.equipment.trim()) m.push('equipment')
  if (!c.materials.trim()) m.push('materials')

  // Триггер обязательности блока «Сотрудник» — заполненное ФИО (MR !13). `personId` остаётся в
  // триггере: у строки, связанной с сотрудником структуры, потерянное ФИО — дефект данных, а не
  // «место без сотрудника».
  const personAssigned = Boolean(line.personId) || Boolean(line.person.fullName.trim())
  if (personAssigned) {
    if (!validateFio(line.person.fullName)) m.push('fullName')
    if (!isValidSnils(line.person.snils)) m.push('snils')
    if (line.person.gender !== 'm' && line.person.gender !== 'f') m.push('gender')
  }

  if (c.transport.some((t) => !isValidTransport(t))) m.push('transport')
  if (!accidentsValid(c.accidents)) m.push('accidents')
  return m
}

/** Валидность строки = нет незаполненных обязательных полей. */
export function isLineValid(line: Pick<SoutLine, 'person' | 'conditions'> & { personId?: string | null }): boolean {
  return computeLineMissing(line).length === 0
}

// --- Предыдущая спецоценка (§8, D5.1) ---

/**
 * Фолбэк-проекция `SoutConditions.previousClass/rmNumber` в `SoutPreviousSout`, когда бэкенд
 * ещё не прислал вычисляемый блок `line.previousSout` (back-compat с фикстурами тестов и
 * старыми моками, где поле опущено) — зеркало серверного `previousSoutFromConditions` для
 * случая пустого источника: `source="none"`, `editable=true` (свободный ввод).
 */
export function derivePreviousSout(c: Pick<SoutConditions, 'previousClass' | 'rmNumber'>): SoutPreviousSout {
  return { class: c.previousClass, rmNumber: c.rmNumber, source: 'none', editable: true }
}

// --- Дашборд «Спецоценка» ---

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Класс УТ 1 или 2 — декларируемое место: срок «бессрочно» (§3d). Правило РЕЗУЛЬТАТА ЗАКАЗА
 * СОУТ, зеркало серверной `domain.ComputeSoutValidity`; сервер шлёт `perpetual`, функция нужна
 * для проверки/выгрузки и тестов.
 *
 * НЕ применять к ИСТОРИЧЕСКОЙ записи: с centrika-docs!29 у неё своё правило — бессрочность даёт
 * только отмеченная декларация (`historicalValidUntilPreview`/`historicalDeclarationAllowed`).
 */
export function isPerpetualClass(hazardClass: string): boolean {
  return hazardClass === '1' || hazardClass === '2'
}

/**
 * Статус места по результату СОУТ (§3d; сервер вычисляет авторитетно, здесь — зеркало для тестов
 * и согласованности). `none` — нет класса; `valid` — perpetual ИЛИ validUntil > now+threshold;
 * `soon` — validUntil ∈ [now, now+threshold]; `overdue` — validUntil < now.
 */
export function computeSoutStatus(
  row: { hazardClass: string; validUntil?: string; perpetual: boolean },
  thresholdDays: number,
  now: Date = new Date(),
): SoutDashboardStatus {
  if (!row.hazardClass) return 'none'
  if (row.perpetual) return 'valid'
  if (!row.validUntil) return 'none'
  const until = new Date(row.validUntil).getTime()
  if (Number.isNaN(until)) return 'none'
  const nowMs = now.getTime()
  const thresholdMs = nowMs + thresholdDays * MS_PER_DAY
  if (until < nowMs) return 'overdue'
  if (until <= thresholdMs) return 'soon'
  return 'valid'
}

export interface SoutStatusView {
  label: string
  role: StatusRole
}

/** Ярлык + цветовая роль статуса строки таблицы (§3d). */
export const SOUT_STATUS_VIEW: Record<SoutDashboardStatus, SoutStatusView> = {
  overdue: { label: 'Просрочено', role: 'danger' },
  soon: { label: 'Истекает скоро', role: 'warning' },
  valid: { label: 'Действует', role: 'success' },
  none: { label: 'Нет данных', role: 'neutral' },
}

/** Подписи светофор-счётчиков дашборда (макет; «Не проводилась» — ярлык счётчика для none). */
export const SOUT_COUNTER_LABELS: Record<SoutDashboardStatus, string> = {
  overdue: 'Просрочено',
  soon: 'Истекает скоро',
  valid: 'Действует',
  none: 'Не проводилась',
}

/**
 * Текст колонки «действует до»: «бессрочно» для класса 1–2 (perpetual); «не проводилась», если
 * нет результата; иначе форматированная дата (форматирование — у вызывающего).
 */
export function validUntilText(
  row: { hazardClass: string; perpetual: boolean; validUntil?: string },
): { kind: 'perpetual' | 'none' | 'date'; date?: string } {
  if (!row.hazardClass) return { kind: 'none' }
  if (row.perpetual) return { kind: 'perpetual' }
  if (!row.validUntil) return { kind: 'none' }
  return { kind: 'date', date: row.validUntil }
}

/**
 * Кнопка «Заказать» показывается на всех статусах, КРОМЕ «Действует» (§3d):
 * overdue/soon/none → true, valid → false.
 */
export function canOrderSout(status: SoutDashboardStatus): boolean {
  return status !== 'valid'
}

// --- Историческая (вне заказа) запись СОУТ (p2-manual-historical, §11.2) ---

export interface HistoricalValidityPreview {
  kind: 'perpetual' | 'date' | 'none'
  /** ISO-дата (ГГГГ-ММ-ДД), только при kind==='date'. */
  date?: string
}

/**
 * Признак декларации доступен только для классов условий труда «1» и «2»
 * (centrika-docs!29, shared/Историческая-СОУТ) — зеркало `domain.SoutDeclarationAllowed`.
 * Для «3.1»…«4» чекбокс «Декларация» недоступен и не выбран.
 *
 * Совпадает по значению с `isPerpetualClass`, но это РАЗНЫЕ правила: `isPerpetualClass`
 * — бессрочность результата ЗАКАЗА (§3d, не менялась), здесь — доступность чекбокса
 * исторической записи (бессрочность которой теперь требует отмеченной декларации).
 */
export function historicalDeclarationAllowed(hazardClass: string): boolean {
  return hazardClass === '1' || hazardClass === '2'
}

/**
 * Состояние чекбокса «Декларация» при повторном открытии СОХРАНЁННОЙ записи
 * (centrika-docs!29, «Изменение исторической записи»): выбран, если для записи с классом
 * «1»/«2» сохранён бессрочный срок. Зеркало `domain.HistoricalSoutDeclaration` — сервер
 * присылает готовый `declaration`, эта функция нужна для записей из старых ответов/тестов.
 */
export function historicalDeclarationChecked(row: { hazardClass: string; perpetual: boolean }): boolean {
  return row.perpetual && historicalDeclarationAllowed(row.hazardClass)
}

/**
 * Клиентское зеркало-предпросмотр серверного `domain.ComputeHistoricalSoutValidity`
 * (centrika-docs!29): для любого класса срок = дата проведения + 5 лет; «бессрочно» —
 * только когда для класса «1»/«2» отмечена декларация. НЕ авторитетно — сервер
 * пересчитывает и сохраняет своё значение; здесь только для read-only предпросмотра
 * формы ДО сохранения (см. `sout/historical-sout-form.tsx`).
 */
export function historicalValidUntilPreview(
  hazardClass: string,
  conductedOn: string,
  declaration = false,
): HistoricalValidityPreview {
  if (declaration && historicalDeclarationAllowed(hazardClass)) return { kind: 'perpetual' }
  if (!conductedOn) return { kind: 'none' }
  const d = new Date(conductedOn)
  if (Number.isNaN(d.getTime())) return { kind: 'none' }
  d.setUTCFullYear(d.getUTCFullYear() + 5)
  return { kind: 'date', date: d.toISOString().slice(0, 10) }
}

// --- Результат заказа СОУТ (команда): класс УТ + декларация (centrika-docs!74/75/76) ---

/**
 * Допустимые значения выпадающего списка «Класс УТ» результата заказа (centrika-docs!76,
 * дословно): по умолчанию значение не выбрано (пустая строка в драфте).
 */
export const SOUT_HAZARD_CLASSES: readonly string[] = ['1', '2', '3.1', '3.2', '3.3', '3.4', '4']

/**
 * Чекбокс «Декларация» результата ЗАКАЗА СОУТ доступен только для классов «1»/«2»
 * (centrika-docs!76) — зеркало `domain.SoutDeclarationAllowed`. Ведётся ОТДЕЛЬНО от
 * исторической записи (`historicalDeclarationAllowed`): у путей своя семантика срока, хотя
 * условие доступности чекбокса по значению совпадает.
 */
export function soutResultDeclarationAllowed(hazardClass: string): boolean {
  return hazardClass === '1' || hazardClass === '2'
}

/**
 * Состояние чекбокса «Декларация» СОХРАНЁННОГО результата заказа при повторном открытии —
 * зеркало `domain.SoutResultDeclaration`: выбран у класса «1»/«2» с бессрочным сроком.
 * Сервер присылает готовый `declaration`; функция нужна для результатов из старых ответов.
 */
export function soutResultDeclarationChecked(row: { hazardClass: string; perpetual: boolean }): boolean {
  return row.perpetual && soutResultDeclarationAllowed(row.hazardClass)
}

/**
 * Клиентское зеркало-предпросмотр `domain.ComputeSoutValidityDeclared` (centrika-docs!76,
 * BREAKING): бессрочно ТОЛЬКО при отмеченной декларации класса «1»/«2»; иначе срок = дата
 * завершения + 5 лет. Сам класс «1»/«2» больше НЕ делает результат бессрочным. НЕ авторитетно
 * — сервер пересчитывает и сохраняет своё значение; здесь только предпросмотр «Действует до».
 */
export function soutResultValidUntilPreview(
  hazardClass: string,
  completionDate: string,
  declaration = false,
): HistoricalValidityPreview {
  if (!hazardClass) return { kind: 'none' }
  if (declaration && soutResultDeclarationAllowed(hazardClass)) return { kind: 'perpetual' }
  if (!completionDate) return { kind: 'none' }
  const d = new Date(completionDate)
  if (Number.isNaN(d.getTime())) return { kind: 'none' }
  d.setUTCFullYear(d.getUTCFullYear() + 5)
  return { kind: 'date', date: d.toISOString().slice(0, 10) }
}

// --- Загрузка результатов из сводной ведомости (sout-results-statement-import) ---

/** Кнопка загрузки ведомости над таблицей «Результат СОУТ по местам». */
export const SOUT_STATEMENT_UPLOAD_LABEL = 'Загрузить сводную ведомость'
/** Подпись кнопки, пока сервер разбирает файл. */
export const SOUT_STATEMENT_UPLOADING_LABEL = 'Распознавание…'
/** Заголовок окна сопоставления. */
export const SOUT_STATEMENT_DIALOG_TITLE = 'Результаты из сводной ведомости'
/** Пояснение в окне сопоставления. */
export const SOUT_STATEMENT_DIALOG_HINT =
  'Проверьте, каким рабочим местам заказа соответствуют строки ведомости. В таблицу подставятся «N раб.места» и класс УТ — с учётом СИЗ, если он указан в ведомости, иначе итоговый, а в «Дату завершения» — дата составления ведомости. Сохраните их кнопкой «Сохранить результаты».'
export const SOUT_STATEMENT_APPLY_LABEL = 'Подставить в таблицу'
export const SOUT_STATEMENT_CANCEL_LABEL = 'Отмена'
/** Вариант выпадающего списка «Рабочее место заказа», когда строку подставлять не нужно. */
export const SOUT_STATEMENT_SKIP_LABEL = 'Не подставлять'
/** Подсказка над таблицей после подстановки значений. */
export const SOUT_STATEMENT_APPLIED_HINT =
  'Значения из сводной ведомости подставлены в таблицу. Проверьте их и нажмите «Сохранить результаты».'
/** Выбран файл не того формата (проверка до отправки; сервер проверяет так же). */
export const SOUT_STATEMENT_FORMAT_ERROR = 'Загрузите сводную ведомость в формате .docx'
/** Общая ошибка разбора, если сервер не прислал причину по полю файла. */
export const SOUT_STATEMENT_PARSE_ERROR = 'Не удалось разобрать сводную ведомость'
/** Метка строки, сопоставленной по порядку среди одинаковых мест. */
export const SOUT_STATEMENT_AMBIGUOUS_LABEL = 'проверьте'
/** Метка строки, место заказа для которой выбрано в окне вручную (не предложенное сервером). */
export const SOUT_STATEMENT_MANUAL_LABEL = 'выбрано вручную'
/** Метка строки, класс которой не распознан (класс не подставляется, номер — подставляется). */
export const SOUT_STATEMENT_CLASS_UNRECOGNIZED_LABEL = 'класс не распознан'

/** Метки правил сопоставления строки ведомости с местом заказа. */
export const SOUT_STATEMENT_MATCH_LABELS: Record<SoutStatementMatch, string> = {
  rm_number: 'по номеру',
  department_position: 'по подразделению и должности',
  position: 'по должности',
  none: 'не найдено',
}

/** Сопоставление строк ведомости с местами заказа: `rowIndex` → `lineId` (`''` — не подставлять). */
export type SoutStatementAssignment = Record<number, string>

/** Начальное сопоставление — предложенное сервером. */
export function soutStatementInitialAssignment(rows: SoutStatementRow[]): SoutStatementAssignment {
  const out: SoutStatementAssignment = {}
  for (const row of rows) out[row.rowIndex] = row.lineId ?? ''
  return out
}

/**
 * Назначает строке ведомости место заказа. Одно место — не больше одной строки: если место уже
 * выбрано в другой строке, там оно снимается («Не подставлять»).
 */
export function soutStatementAssign(
  assignment: SoutStatementAssignment,
  rowIndex: number,
  lineId: string,
): SoutStatementAssignment {
  const out: SoutStatementAssignment = { ...assignment }
  if (lineId) {
    for (const key of Object.keys(out)) {
      if (out[Number(key)] === lineId) out[Number(key)] = ''
    }
  }
  out[rowIndex] = lineId
  return out
}

/** Значения для подстановки в строку таблицы результатов. */
export interface SoutStatementApplyItem {
  lineId: string
  rmNumber: string
  /** Класс УТ; `null` — класс не распознан, подставляется только номер. */
  hazardClass: string | null
  /**
   * Должность места в формулировке ведомости. В таблицу не подставляется: по ней декларация
   * сверяет «номер + должность» — ведомость и декларация выгружены из одной программы.
   */
  position: string
}

/** Значения для подстановки по выбранному сопоставлению, в порядке строк ведомости. */
export function soutStatementApplyItems(
  rows: SoutStatementRow[],
  assignment: SoutStatementAssignment,
): SoutStatementApplyItem[] {
  const items: SoutStatementApplyItem[] = []
  for (const row of rows) {
    const lineId = assignment[row.rowIndex]
    if (!lineId) continue
    items.push({
      lineId,
      rmNumber: row.rmNumber,
      hazardClass: row.classRecognized && row.hazardClass ? row.hazardClass : null,
      position: row.position,
    })
  }
  return items
}

/**
 * Предупреждение о неполном разборе: в сводной таблице ведомости указано одно число рабочих мест, а
 * распознано другое. `null` — числа совпадают или в ведомости числа нет.
 */
export function soutStatementCountWarning(statement: SoutStatement): string | null {
  const declared = statement.declaredWorkplaces
  const parsed = statement.rows.length
  if (declared <= 0 || declared === parsed) return null
  return `Рабочих мест в ведомости: ${declared}, распознано: ${parsed}. Проверьте файл.`
}

/** ДД.ММ.ГГГГ из ГГГГ-ММ-ДД (для подписей окон загрузки). */
function isoToRu(iso: string): string {
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso
}

/**
 * Что произойдёт с общей «Датой завершения» при подстановке ведомости
 * (sout-results-declaration-and-statement-date): в неё тянется дата составления ведомости.
 */
export function soutStatementDateNote(compiledOn: string, completionDate: string): string {
  if (!compiledOn) {
    return 'Дата составления в ведомости не найдена — «Дата завершения» не изменится, укажите её вручную.'
  }
  if (!completionDate) return `«Дата завершения» будет установлена: ${isoToRu(compiledOn)}.`
  if (completionDate === compiledOn) return `«Дата завершения» уже ${isoToRu(compiledOn)}.`
  return `«Дата завершения» будет изменена с ${isoToRu(completionDate)} на ${isoToRu(compiledOn)}.`
}

// --- Отметка декларации из файла декларации (sout-results-declaration-and-statement-date) ---

/** Кнопка загрузки декларации над таблицей «Результат СОУТ по местам». */
export const SOUT_DECLARATION_UPLOAD_LABEL = 'Загрузить декларацию'
export const SOUT_DECLARATION_DIALOG_TITLE = 'Рабочие места из декларации'
export const SOUT_DECLARATION_DIALOG_HINT =
  'Место декларации сверяется с таблицей по «N раб.места» и должности. У отмеченных мест в таблице будет поставлена «Декларация» — сохраните её кнопкой «Сохранить результаты». Декларация возможна только для класса УТ 1 или 2; у мест, которых нет в декларации, отметка не снимается.'
export const SOUT_DECLARATION_APPLY_LABEL = 'Отметить декларацию'
/** Подсказка над таблицей после отметки. */
export const SOUT_DECLARATION_APPLIED_HINT =
  'Декларация отмечена у рабочих мест из файла. Проверьте и нажмите «Сохранить результаты».'
export const SOUT_DECLARATION_FORMAT_ERROR = 'Загрузите декларацию в формате .docx'
export const SOUT_DECLARATION_PARSE_ERROR = 'Не удалось разобрать декларацию'
/** Предупреждение, если ИНН декларации не совпал с организацией заказа. */
export const SOUT_DECLARATION_INN_MISMATCH =
  'ИНН в декларации не совпадает с ИНН организации заказа — проверьте, что загружена декларация этого клиента.'

/** Итог сверки места декларации с таблицей результатов. */
export type SoutDeclarationMatchStatus =
  /** Совпали номер и должность. */
  | 'matched'
  /** Номер совпал, должность — нет: нужна проверка. */
  | 'position_mismatch'
  /** Места с таким номером в таблице нет. */
  | 'not_found'

/** Метки итогов сверки. */
export const SOUT_DECLARATION_STATUS_LABELS: Record<SoutDeclarationMatchStatus, string> = {
  matched: 'номер и должность совпали',
  position_mismatch: 'должность не совпала — проверьте',
  not_found: 'нет в таблице',
}

/** Место таблицы результатов, с которым сверяется декларация. */
export interface SoutDeclarationTableRow {
  lineId: string
  /** «N раб.места» из поля таблицы (в т.ч. ещё не сохранённый — после подстановки ведомости). */
  rmNumber: string
  /** Должности места для сверки: формулировка ведомости (если загружалась) и анкеты. */
  positions: string[]
  hazardClass: string
}

/** Сверка одного места декларации. */
export interface SoutDeclarationMatch {
  workplace: SoutDeclarationWorkplace
  /** Место таблицы с тем же номером; null — не найдено. */
  lineId: string | null
  status: SoutDeclarationMatchStatus
  /** Класс места позволяет декларацию («1»/«2»). */
  classAllowed: boolean
}

/**
 * Текст для сравнения номеров и должностей — зеркало `domain.normalizeSoutStatementText`: нижний
 * регистр, «ё» как «е», без кавычек, тире как дефис, пробелы схлопнуты и убраны вокруг дефиса, без
 * точки в конце.
 */
export function soutMatchText(s: string): string {
  let out = s
    .toLowerCase()
    .replace(/[«»"„“”]/g, '')
    .replace(/ё/g, 'е')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
  out = out.replace(/ -/g, '-').replace(/- /g, '-')
  return out.replace(/\.$/, '').trim()
}

/**
 * Сверяет места декларации с таблицей результатов по связке «номер + должность» (решение
 * заказчика). Номер ищется среди «N раб.места» таблицы; одно место таблицы достаётся не более чем
 * одному месту декларации (одинаковый номер у двух строк таблицы — берётся та, где совпала и
 * должность, иначе первая).
 */
export function soutDeclarationMatches(
  workplaces: SoutDeclarationWorkplace[],
  rows: SoutDeclarationTableRow[],
): SoutDeclarationMatch[] {
  const used = new Set<string>()
  return workplaces.map((workplace) => {
    const number = soutMatchText(workplace.rmNumber)
    const position = soutMatchText(workplace.position)
    const candidates = number
      ? rows.filter((r) => !used.has(r.lineId) && soutMatchText(r.rmNumber) === number)
      : []
    const samePosition = candidates.find((r) =>
      r.positions.some((p) => p && soutMatchText(p) === position),
    )
    const row = samePosition ?? candidates[0]
    if (!row) return { workplace, lineId: null, status: 'not_found', classAllowed: false }
    used.add(row.lineId)
    return {
      workplace,
      lineId: row.lineId,
      status: samePosition ? 'matched' : 'position_mismatch',
      classAllowed: soutResultDeclarationAllowed(row.hazardClass),
    }
  })
}

/**
 * Начальные отметки окна декларации: места, где совпали номер и должность и класс позволяет
 * декларацию. Места «проверьте» пользователь отмечает сам. Ключ — `workplace.index`.
 */
export function soutDeclarationInitialSelection(matches: SoutDeclarationMatch[]): Set<number> {
  return new Set(
    matches.filter((m) => m.status === 'matched' && m.classAllowed).map((m) => m.workplace.index),
  )
}

/** Места таблицы, у которых нужно поставить «Декларацию», по отметкам окна. */
export function soutDeclarationApplyLineIds(
  matches: SoutDeclarationMatch[],
  selected: Set<number>,
): string[] {
  return matches
    .filter((m) => m.lineId && m.classAllowed && selected.has(m.workplace.index))
    .map((m) => m.lineId as string)
}

// --- Гейт подачи/выгрузки (повторный прогон всех проверок, §3d) ---

export interface AnketaValidation {
  /** Всё валидно: можно подать заказ и выгрузить в Excel. */
  ok: boolean
  commissionError: string | null
  responsibleErrors: ResponsibleErrors
  pdConsent: boolean
  hasLines: boolean
  /** Число недозаполненных мест (для счётчика «дозаполнить N шт.»). */
  invalidLineCount: number
}

interface AnketaLike {
  commission: CommissionMember[]
  responsible: Responsible
  pdConsent: boolean
  lines: Array<Pick<SoutLine, 'person' | 'conditions'> & { personId?: string | null }>
}

/**
 * Повторный прогон всех проверок анкеты перед подачей/выгрузкой (§3d «Повторная проверка»).
 * Гейт ПД: без `pdConsent` подача и выгрузка запрещены, даже если всё остальное валидно.
 */
export function validateAnketa(a: AnketaLike): AnketaValidation {
  const commissionError = validateCommission(a.commission)
  const responsibleErrors = validateResponsible(a.responsible)
  const invalidLineCount = a.lines.filter((l) => !isLineValid(l)).length
  const hasLines = a.lines.length > 0
  const ok =
    commissionError === null &&
    Object.keys(responsibleErrors).length === 0 &&
    a.pdConsent === true &&
    hasLines &&
    invalidLineCount === 0
  return { ok, commissionError, responsibleErrors, pdConsent: a.pdConsent, hasLines, invalidLineCount }
}

/** Можно ли подать заказ / выгрузить в Excel (полный гейт, включая ПД-согласие). */
export function canSubmitAnketa(a: AnketaLike): boolean {
  return validateAnketa(a).ok
}
